<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'

import { useNotificationsStore } from '@/stores/notifications'
import { formatNumber } from '@/utils/format'
import NotificationList from './NotificationList.vue'

// The toolbar bell: an unread badge, and a menu with the notification list. Opening it refreshes the feed.
const notificationsStore = useNotificationsStore()
const { unreadCount } = storeToRefs(notificationsStore)

const menuOpen = ref(false)
const badgeText = computed(() => (unreadCount.value > 99 ? '99+' : formatNumber(unreadCount.value)))
const label = computed(() =>
  unreadCount.value > 0 ? `Notifications, ${unreadCount.value} unread` : 'Notifications'
)
</script>

<template>
  <q-btn
    dense
    flat
    round
    icon="mdi-bell-outline"
    class="q-mr-sm"
    :aria-label="label"
    data-testid="notification-bell"
  >
    <q-badge
      v-if="unreadCount > 0"
      color="red"
      text-color="white"
      floating
      rounded
      data-testid="notification-badge"
    >
      {{ badgeText }}
    </q-badge>
    <q-menu
      v-model="menuOpen"
      anchor="bottom right"
      self="top right"
      :offset="[0, 6]"
      class="notification-menu"
      @before-show="notificationsStore.refresh()"
    >
      <notification-list @navigate="menuOpen = false" />
    </q-menu>
  </q-btn>
</template>

<style>
/* The menu is teleported to the page body, so this style cannot be scoped. */
.notification-menu {
  width: 380px;
  max-width: calc(100vw - 16px);
}

/* Phones: span the screen instead of hugging the bell, which sits right of centre. */
@media (max-width: 599px) {
  .notification-menu {
    left: 8px !important;
    right: 8px !important;
    width: auto !important;
    max-width: none !important;
  }
}
</style>
